import React, { useEffect, useState, useCallback, useRef } from "react";
import { useApp } from "../../context/AppContext";
import api from "../../api";
import { toast } from "sonner";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Upload, Link2, Unlink, Trash2 } from "lucide-react";

const errMsg = (e, t) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : t("msg.error"); };

export const BankTab = ({ causali, reload }) => {
  const { t, fmt, entity } = useApp();
  const [data, setData] = useState({ movements: [], unmatched: 0, balance: 0 });
  const [filter, setFilter] = useState("all");
  const [pick, setPick] = useState({});
  const fileRef = useRef(null);
  const load = useCallback(() => api.get(`/bank/movements?entity_id=${entity}`).then((r) => setData(r.data)).catch(() => {}), [entity]);
  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("entity_id", entity); fd.append("file", file);
    try { const r = await api.post("/bank/import", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`${t("bank.imported")}: ${r.data.imported} (${t("bank.dup")}: ${r.data.duplicates})`); load(); }
    catch (e) { toast.error(errMsg(e, t)); } finally { if (fileRef.current) fileRef.current.value = ""; }
  };
  const match = async (m, body) => { try { await api.post(`/bank/movements/${m.id}/match`, body); toast.success(t("bank.matched")); load(); reload(); } catch (e) { toast.error(errMsg(e, t)); } };
  const unmatch = async (m) => { try { await api.post(`/bank/movements/${m.id}/unmatch`); load(); } catch (e) { toast.error(errMsg(e, t)); } };
  const remove = async (m) => { try { await api.delete(`/bank/movements/${m.id}`); load(); } catch (e) { toast.error(errMsg(e, t)); } };
  const applyPick = (m) => {
    const v = pick[m.id]; if (!v) return;
    const [type, id] = v.split(":");
    match(m, type === "invoice" ? { invoice_id: id } : type === "journal" ? { journal_id: id } : { causale_id: id });
  };
  const rows = data.movements.filter((m) => filter === "all" || m.status === filter);
  const simpleCausali = causali.filter((c) => c.lines.every((l) => l.amount_type !== "manual") && c.lines.some((l) => l.account_code === "1000"));

  return (
    <div className="space-y-4" data-testid="bank-tab">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline" data-testid="bank-unmatched">{t("bank.unmatched")}: {data.unmatched}</Badge>
        <Badge variant="outline">{t("bank.netFlow")}: <span className="font-mono ml-1">{fmt(data.balance)}</span></Badge>
        <Select value={filter} onValueChange={setFilter}><SelectTrigger className="w-40 h-9" data-testid="bank-filter-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">{t("bank.all")}</SelectItem><SelectItem value="unmatched">{t("bank.unmatched")}</SelectItem><SelectItem value="matched">{t("bank.matched")}</SelectItem></SelectContent></Select>
        <div className="flex-1" />
        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="bank-file-input" />
        <Button onClick={() => fileRef.current?.click()} data-testid="bank-import-button"><Upload className="h-4 w-4 mr-2" />{t("bank.import")}</Button>
      </div>
      <p className="text-xs text-muted-foreground">{t("bank.hint")}</p>
      <Card><CardContent className="p-0">
        {rows.length === 0 ? <div className="p-6 text-sm text-muted-foreground">{t("common.noData")}</div> : (
          <Table>
            <TableHeader><TableRow><TableHead>{t("common.date")}</TableHead><TableHead>{t("common.description")}</TableHead><TableHead className="text-right">{t("common.amount")}</TableHead><TableHead>{t("common.status")}</TableHead><TableHead className="min-w-[320px]">{t("bank.matchWith")}</TableHead><TableHead className="w-24" /></TableRow></TableHeader>
            <TableBody>
              {rows.map((m) => (
                <TableRow key={m.id} data-testid={`bank-row-${m.id}`}>
                  <TableCell className="font-mono text-xs">{m.date}</TableCell>
                  <TableCell className="text-sm max-w-[320px] truncate" title={m.description}>{m.description}</TableCell>
                  <TableCell className={`text-right font-mono text-sm ${m.amount < 0 ? "text-red-500" : "text-emerald-500"}`}>{fmt(m.amount)}</TableCell>
                  <TableCell><Badge variant={m.status === "matched" ? "default" : "secondary"} className="text-[10px]" data-testid={`bank-status-${m.id}`}>{t(`bank.${m.status}`)}</Badge></TableCell>
                  <TableCell>
                    {m.status === "matched" ? <span className="text-xs text-muted-foreground">{m.match?.type === "invoice" ? t("bank.invoice") : t("bank.journal")}{m.match?.causale_code ? ` · ${m.match.causale_code}` : ""}</span> : (
                      <div className="flex gap-1">
                        <Select value={pick[m.id] || ""} onValueChange={(v) => setPick({ ...pick, [m.id]: v })}>
                          <SelectTrigger className="h-8 text-xs" data-testid={`bank-pick-${m.id}`}><SelectValue placeholder={t("bank.choose")} /></SelectTrigger>
                          <SelectContent>
                            {(m.suggestions || []).map((s) => <SelectItem key={`${s.type}:${s.id}`} value={`${s.type}:${s.id}`} data-testid={`bank-sugg-${m.id}-${s.type}`}>★ {s.label}</SelectItem>)}
                            {simpleCausali.map((c) => <SelectItem key={`causale:${c.id}`} value={`causale:${c.id}`}>{c.code} · {c.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Button size="icon" className="h-8 w-8" disabled={!pick[m.id]} onClick={() => applyPick(m)} data-testid={`bank-match-${m.id}`}><Link2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    )}
                  </TableCell>
                  <TableCell><div className="flex gap-1">
                    {m.status === "matched" && <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => unmatch(m)} data-testid={`bank-unmatch-${m.id}`}><Unlink className="h-3.5 w-3.5" /></Button>}
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => remove(m)} data-testid={`bank-delete-${m.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                  </div></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>
    </div>
  );
};
